/** Pure board-state helpers (unit tested; no React, no I/O). */
import type { BoardDetail, Card, Column } from './types';

export function cardsInColumn(detail: BoardDetail, columnId: string): Card[] {
  return detail.cards.filter((card) => card.placement.column_id === columnId);
}

/** Card drop target: before/after an anchor card, or the end of the column. */
export type CardTarget =
  | { columnId: string; anchorCardId: string; edge: 'before' | 'after' }
  | { columnId: string; anchorCardId: null };

export interface CardMoveRequest {
  column_id: string;
  after_card_id?: string;
  before_card_id?: string;
}

export function cardMoveRequest(target: CardTarget): CardMoveRequest {
  if (target.anchorCardId === null) return { column_id: target.columnId };
  return target.edge === 'after'
    ? { column_id: target.columnId, after_card_id: target.anchorCardId }
    : { column_id: target.columnId, before_card_id: target.anchorCardId };
}

/**
 * Pinned and unpinned cards are separate sections of a column (backend rule:
 * an anchor must be in the moved card's section). A drop that crosses the
 * boundary is snapped to the edge of the moved card's own section.
 */
export function normalizeCardTarget(
  detail: BoardDetail,
  cardId: string,
  target: CardTarget,
): CardTarget {
  const card = detail.cards.find((c) => c.id === cardId);
  if (!card || target.anchorCardId === null) return target;
  const anchor = detail.cards.find((c) => c.id === target.anchorCardId);
  if (!anchor || anchor.placement.is_pinned === card.placement.is_pinned) return target;
  const columnCards = cardsInColumn(detail, target.columnId).filter((c) => c.id !== cardId);
  if (card.placement.is_pinned) {
    // A pinned card dropped among unpinned ones: end of the pinned section.
    return { columnId: target.columnId, anchorCardId: null };
  }
  // An unpinned card dropped among pinned ones: start of the unpinned section.
  const firstUnpinned = columnCards.find((c) => !c.placement.is_pinned);
  return firstUnpinned
    ? { columnId: target.columnId, anchorCardId: firstUnpinned.id, edge: 'before' }
    : { columnId: target.columnId, anchorCardId: null };
}

/**
 * Board state after moving `cardId` to `target`, or `null` if nothing would change
 * (dropping a card on itself or onto its current spot). `target` must be normalized.
 */
export function moveCardLocally(
  detail: BoardDetail,
  cardId: string,
  target: CardTarget,
): BoardDetail | null {
  const card = detail.cards.find((c) => c.id === cardId);
  if (!card || target.anchorCardId === cardId) return null;

  const rest = detail.cards.filter((c) => c.id !== cardId);
  const moved: Card = { ...card, placement: { ...card.placement, column_id: target.columnId } };

  let index: number;
  if (target.anchorCardId === null) {
    // End of the card's section in the target column.
    const sameSection = (c: Card) =>
      c.placement.column_id === target.columnId &&
      (!card.placement.is_pinned || c.placement.is_pinned);
    const last = rest.findLastIndex(sameSection);
    const firstInColumn = rest.findIndex((c) => c.placement.column_id === target.columnId);
    index = last !== -1 ? last + 1 : firstInColumn !== -1 ? firstInColumn : rest.length;
  } else {
    const anchorIndex = rest.findIndex((c) => c.id === target.anchorCardId);
    if (anchorIndex === -1) return null;
    index = target.edge === 'after' ? anchorIndex + 1 : anchorIndex;
  }

  const cards = [...rest.slice(0, index), moved, ...rest.slice(index)];
  const unchanged =
    cards.every((c, i) => c.id === detail.cards[i]?.id) &&
    card.placement.column_id === target.columnId;
  return unchanged ? null : { ...detail, cards };
}

/** Optimistic pin/unpin: pinned cards first (new pin last), the rest by position key. */
export function setPinnedLocally(
  detail: BoardDetail,
  cardId: string,
  pinned: boolean,
): BoardDetail {
  const card = detail.cards.find((c) => c.id === cardId);
  if (!card || card.placement.is_pinned === pinned) return detail;
  const columnId = card.placement.column_id;
  const updated: Card = { ...card, placement: { ...card.placement, is_pinned: pinned } };
  const column = detail.cards
    .filter((c) => c.placement.column_id === columnId && c.id !== cardId)
    .concat(updated);
  const pinnedCards = column.filter((c) => c.placement.is_pinned);
  const regular = column
    .filter((c) => !c.placement.is_pinned)
    .sort((a, b) =>
      a.placement.position_key < b.placement.position_key
        ? -1
        : a.placement.position_key > b.placement.position_key
          ? 1
          : 0,
    );
  const ordered = [...pinnedCards, ...regular];
  const firstIndex = detail.cards.findIndex((c) => c.placement.column_id === columnId);
  const others = detail.cards.filter((c) => c.placement.column_id !== columnId);
  return {
    ...detail,
    cards: [...others.slice(0, firstIndex), ...ordered, ...others.slice(firstIndex)],
  };
}

/**
 * The moment a card sorts by in the system Calendar column: the event START, else the deadline
 * END (both absolute instants the server derived), or `null` for an undated card.
 */
function calendarMoment(card: Card): number | null {
  const iso = card.deadline?.start_at ?? card.deadline?.at ?? null;
  if (iso === null) return null;
  const time = Date.parse(iso);
  return Number.isNaN(time) ? null : time;
}

/**
 * Display order of the system Calendar column - the same rule as the server's read model:
 * chronological, undated last, ties keep the order they arrived in (the stored position). Pinning
 * does NOT override it here (a pinned card keeps its pin, not a place above earlier dates).
 * Pure and derived: nothing is persisted.
 */
export function orderCalendarColumn(cards: readonly Card[]): Card[] {
  return cards
    .map((card, index) => ({ card, index, at: calendarMoment(card) }))
    .sort((a, b) => {
      if (a.at === null || b.at === null) {
        if (a.at === b.at) return a.index - b.index;
        return a.at === null ? 1 : -1;
      }
      return a.at - b.at || a.index - b.index;
    })
    .map((entry) => entry.card);
}

export type ColumnTarget = { anchorColumnId: string; edge: 'before' | 'after' };

export function columnMoveRequest(target: ColumnTarget) {
  return target.edge === 'after'
    ? { after_column_id: target.anchorColumnId }
    : { before_column_id: target.anchorColumnId };
}

export function moveColumnLocally(
  detail: BoardDetail,
  columnId: string,
  target: ColumnTarget,
): BoardDetail | null {
  const column = detail.columns.find((c) => c.id === columnId);
  if (!column || target.anchorColumnId === columnId) return null;
  const rest = detail.columns.filter((c) => c.id !== columnId);
  const anchorIndex = rest.findIndex((c) => c.id === target.anchorColumnId);
  if (anchorIndex === -1) return null;
  const index = target.edge === 'after' ? anchorIndex + 1 : anchorIndex;
  const columns = [...rest.slice(0, index), column, ...rest.slice(index)];
  const unchanged = columns.every((c, i) => c.id === detail.columns[i]?.id);
  return unchanged ? null : { ...detail, columns };
}

/**
 * Neighbor-based target to move a column one step left/right (non-DnD control). `shown` are
 * the columns the user actually sees: the neighbor is taken from them, so a hidden column (the
 * system Calendar column) never swallows a "move" click.
 */
export function adjacentColumnTarget(
  detail: BoardDetail,
  columnId: string,
  direction: 'left' | 'right',
  shown: readonly Column[] = detail.columns,
): ColumnTarget | null {
  const index = shown.findIndex((c) => c.id === columnId);
  const neighbor = shown[direction === 'left' ? index - 1 : index + 1];
  if (index === -1 || !neighbor) return null;
  return { anchorColumnId: neighbor.id, edge: direction === 'left' ? 'before' : 'after' };
}

export interface ColumnVisibility {
  /** Ids of cards matching the active filter; `null` when nothing is filtered. */
  visibleCardIds: ReadonlySet<string> | null;
  /**
   * A filter or text search is active AND its result is known: ordinary columns without any
   * matching card are hidden (a presentation rule - nothing is changed on the server).
   */
  filterActive: boolean;
}

/**
 * The columns Kanban shows, in board order. Pure view logic: nothing here touches the server.
 *
 * - The system Calendar column follows the user's explicit preference (`show_calendar_column`):
 *   hidden stays hidden and shown stays shown, whatever the filters do.
 * - While a filter or search is active, ordinary columns without any matching card are hidden so
 *   the board has no empty gaps. Without a filter every column stays, empty ones included, and
 *   clearing the filter brings every hidden column back.
 */
export function kanbanColumns(detail: BoardDetail, visibility: ColumnVisibility): Column[] {
  const withMatches = matchingColumnIds(detail, visibility);
  return detail.columns.filter((column) => {
    if (column.system_kind === 'calendar') return detail.show_calendar_column;
    return withMatches === null || withMatches.has(column.id);
  });
}

/** How many ordinary columns the active filter hides (the "n columns hidden" notice). */
export function hiddenColumnCount(detail: BoardDetail, visibility: ColumnVisibility): number {
  const withMatches = matchingColumnIds(detail, visibility);
  if (withMatches === null) return 0;
  return detail.columns.filter((c) => c.system_kind === null && !withMatches.has(c.id)).length;
}

function matchingColumnIds(
  detail: BoardDetail,
  { visibleCardIds, filterActive }: ColumnVisibility,
): Set<string> | null {
  if (!filterActive || !visibleCardIds) return null;
  const ids = new Set<string>();
  for (const card of detail.cards) {
    if (visibleCardIds.has(card.id)) ids.add(card.placement.column_id);
  }
  return ids;
}

/** Number of active cards in the system Calendar column (shown on the dock toggle). */
export function calendarColumnCardCount(detail: BoardDetail): number {
  const column = detail.columns.find((c) => c.system_kind === 'calendar');
  return column ? cardsInColumn(detail, column.id).length : 0;
}

/**
 * The name to show for a column. The system Calendar column is identified by `system_kind`, its
 * stored name is only a fallback: the UI shows the localized name.
 */
export function displayColumnName(column: Column, calendarName: string): string {
  return column.system_kind === 'calendar' ? calendarName : column.name;
}

/** The first column that may be a default destination: never the system Calendar column. */
export function firstOrdinaryColumn(columns: readonly Column[]): Column | undefined {
  return columns.find((column) => column.system_kind === null);
}
